import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Gavel, Headphones, Instagram, Link as LinkIcon, Mail,
  MessageCircle, Send, Trophy,
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { renderSettingText } from '../../lib/textTemplate';
import { resolveApiAssetUrl } from '../../lib/api';
import SponsorshipStrip from '../../components/SponsorshipStrip';
import EntryDomainStrip from '../../components/EntryDomainStrip';

// Panelde her menü grubuna bir ikon seçilir (FooterManagement'taki liste ile
// aynı anahtarlar); tanınmayan değer bağlantı ikonuna düşer.
const GROUP_ICONS = {
  'file-text': FileText,
  gavel: Gavel,
  headphones: Headphones,
  trophy: Trophy,
  link: LinkIcon,
};

// Footer içeriği yönetim panelindeki footer_menu_groups / footer_licenses
// kayıtlarından gelir; Herkul şeması bunları dört sütunlu düzende gösterir.
const HerkulFooter = () => {
  const settings = useSettings();
  const menuGroups = settings.footer_menu_groups || [];
  const licenses = (settings.footer_licenses || []).filter((item) => item.image_url);
  // Yatırım yöntemi şeridi footer'ın en üstünde durur; logosu ve etiketi
  // birden boş olan kayıt çizilmez.
  const paymentMethods = (settings.footer_payment_methods || [])
    .filter((item) => item.image_url || item.label);
  // Sponsor şeridi yatırım yöntemlerinin hemen altındadır; logosuz kayıt
  // çizilmez (lisans rozetlerindeki kural).
  const sponsors = (settings.footer_sponsors || []).filter((item) => item.image_url);

  const socials = [
    { url: settings.telegram_url, icon: Send, label: 'Telegram' },
    { url: settings.whatsapp_url, icon: MessageCircle, label: 'WhatsApp' },
    { url: settings.instagram_url, icon: Instagram, label: 'Instagram' },
    { url: settings.contact_email ? `mailto:${settings.contact_email}` : '', icon: Mail, label: 'E-posta' },
  ].filter((item) => item.url);

  return (
    <footer className="herkul-footer">
      {paymentMethods.length > 0 && (
        <div className="herkul-footer-pay">
          <div className="herkul-footer-pay-inner">
            {settings.footer_payment_title && (
              <h2 className="herkul-footer-pay-title">
                {renderSettingText(settings.footer_payment_title, settings)}
              </h2>
            )}
            <div className="herkul-footer-pay-list">
              {paymentMethods.map((method, index) => {
                const label = renderSettingText(method.label || '', settings);
                const content = (
                  <>
                    {method.image_url && (
                      <img
                        src={resolveApiAssetUrl(method.image_url)}
                        alt={label || 'Yatırım yöntemi'}
                        loading="lazy"
                      />
                    )}
                    {label && <span className="herkul-footer-pay-label">{label}</span>}
                  </>
                );
                const key = `${method.label}-${method.image_url}-${index}`;
                return method.link ? (
                  <a
                    key={key}
                    href={method.link}
                    target={method.new_tab ? '_blank' : undefined}
                    rel={method.new_tab ? 'noreferrer' : undefined}
                    className="herkul-footer-pay-item"
                  >
                    {content}
                  </a>
                ) : (
                  <span key={key} className="herkul-footer-pay-item">{content}</span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {sponsors.length > 0 && (
        <div className="herkul-footer-pay herkul-footer-sponsors">
          <div className="herkul-footer-pay-inner">
            {settings.footer_sponsor_title && (
              <h2 className="herkul-footer-pay-title">
                {renderSettingText(settings.footer_sponsor_title, settings)}
              </h2>
            )}
            <div className="herkul-footer-pay-list">
              {sponsors.map((sponsor, index) => {
                const label = renderSettingText(sponsor.name || 'Sponsor', settings);
                const image = (
                  <img
                    src={resolveApiAssetUrl(sponsor.image_url)}
                    alt={label}
                    loading="lazy"
                  />
                );
                const key = `${sponsor.name}-${sponsor.image_url}-${index}`;
                return sponsor.link ? (
                  <a
                    key={key}
                    href={sponsor.link}
                    target={sponsor.new_tab ? '_blank' : undefined}
                    rel={sponsor.new_tab ? 'noreferrer' : undefined}
                    className="herkul-footer-pay-item herkul-footer-sponsor"
                    aria-label={label}
                  >
                    {image}
                  </a>
                ) : (
                  <span key={key} className="herkul-footer-pay-item herkul-footer-sponsor">
                    {image}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="herkul-footer-inner">
        <div className="herkul-footer-cols">
          {menuGroups.map((group, groupIndex) => {
            const GroupIcon = GROUP_ICONS[group.icon] || LinkIcon;
            return (
            <section key={`${group.title}-${groupIndex}`}>
              <h2 className="herkul-footer-title">
                <span className="herkul-footer-title-icon"><GroupIcon size={14} /></span>
                {renderSettingText(group.title, settings)}
              </h2>
              {(group.links || []).map((item, itemIndex) => (
                (item.url || '').startsWith('/') && !item.new_tab ? (
                  <Link key={`${item.label}-${itemIndex}`} to={item.url} className="herkul-footer-link">
                    {renderSettingText(item.label, settings)}
                  </Link>
                ) : (
                  <a
                    key={`${item.label}-${itemIndex}`}
                    href={item.url || '#'}
                    target={item.new_tab ? '_blank' : undefined}
                    rel={item.new_tab ? 'noreferrer' : undefined}
                    className="herkul-footer-link"
                  >
                    {renderSettingText(item.label, settings)}
                  </a>
                )
              ))}
            </section>
            );
          })}

          {/* Menü grupları dört sütunu doldurmuyorsa iletişim son sütuna geçer;
              doldurduğunda tekrar başlık açmayıp sosyal ikonlar aşağıda durur. */}
          {socials.length > 0 && menuGroups.length < 4 && (
            <section>
              <h2 className="herkul-footer-title">
                <span className="herkul-footer-title-icon"><Mail size={14} /></span>
                Bize Ulaşın
              </h2>
              {settings.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="herkul-footer-link">
                  {settings.contact_email}
                </a>
              )}
              <div className="herkul-footer-social">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.label}
                  >
                    <social.icon size={16} />
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        {socials.length > 0 && menuGroups.length >= 4 && (
          <div className="herkul-footer-contacts">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.url}
                target="_blank"
                rel="noreferrer"
                className="herkul-footer-contact"
              >
                <social.icon size={16} /> {social.label}
              </a>
            ))}
          </div>
        )}

        {settings.footer_text && (
          <div className="herkul-footer-seo">
            <p>{renderSettingText(settings.footer_text, settings)}</p>
            {settings.contact_phone && (
              <p>
                Sorularınız için <strong>{settings.contact_phone}</strong> numarasından
                bize ulaşabilir, önerilerinizi iletebilirsiniz.
              </p>
            )}
          </div>
        )}

        <div className="herkul-footer-bottom">
          {licenses.map((license, index) => {
            const label = renderSettingText(license.name || 'Lisans', settings);
            const image = (
              <img
                src={resolveApiAssetUrl(license.image_url)}
                alt={label}
                style={{ maxHeight: 44, width: 'auto', objectFit: 'contain' }}
                loading="lazy"
              />
            );

            return license.link ? (
              <a
                key={`${license.name}-${index}`}
                href={license.link}
                target={license.new_tab ? '_blank' : undefined}
                rel={license.new_tab ? 'noreferrer' : undefined}
                className="herkul-footer-license"
                aria-label={label}
              >
                {image}
              </a>
            ) : (
              <span key={`${license.name}-${index}`} className="herkul-footer-license">
                {image}
              </span>
            );
          })}
          {settings.footer_license_text && (
            <p style={{ maxWidth: 900 }}>
              {renderSettingText(settings.footer_license_text, settings)}
            </p>
          )}
        </div>
      </div>
      {/* Sponsorluk bandı footer'ın en altındadır ve beş kabukta da
          aynı bileşendir; kayıt yoksa hiçbir şey çizmez. */}
      <EntryDomainStrip />
      <SponsorshipStrip />
    </footer>
  );
};

export default HerkulFooter;
